<script setup lang="ts">
import { useFlowControlStore } from '@components/flow-controller'
import { IconExclamationCircle } from '@tabler/icons-vue'
import { computed, ref, watchEffect } from 'vue'

import BaseButton from '@tix/ds/components/atoms/BaseButton.vue'
import StatusMessage from '@tix/ds/components/atoms/StatusMessage.vue'
import type { Schema } from '@tix/openapi-sdk'
import { TixDate } from '@tix/tix-time'

import { getTimezone } from '@/helpers/environment'
import { getErrorForAction } from '@/helpers/flowActionErrors'

import { useBundleStepTracking } from '@/composables/useBundleStepTracking'

import ActionHeader from './ActionHeader.vue'
import SelectableCard from './SelectableCard.vue'
import { openCalendarPickerModal } from './openCalendarPickerModal'
import type { ActionValidationError, SelectableItemBase, WithRequired } from './types'

const actionType: Schema<'action_pick_date'>['type'] = 'pick_date'
const MAX_CARDS = 8

const store = useFlowControlStore()
const { trackBundleStep } = useBundleStepTracking()

// Initialize from previous input if available (e.g. when navigating back)
const prevInput = store.currentActionPreviousInput
const selectedDate = ref<string | null>((prevInput?.type === actionType && prevInput.input.date) || null)
const validationError = ref<ActionValidationError | null>(null)

// Register input getter with the store
store.registerInputGetter(() => {
  if (!selectedDate.value) {
    validationError.value = {
      type: 'client-side',
      message: 'Please select a date to continue.',
    }
    return { isValid: false }
  }

  // Reset errors on every submission attempt
  validationError.value = null

  return { isValid: true, input: { date: selectedDate.value } }
})

// Register error handler for API errors (e.g., date became unavailable)
store.registerErrorHandler((apiError: unknown) => {
  const { message } = getErrorForAction(apiError, actionType)
  if (message) validationError.value = { type: 'server-side', message }
  return Boolean(message)
})

const action = computed(() => (store.action?.type === actionType && store.action) || null)

const title = computed(() => action.value?.meta?.summary_title)
const prompt = computed(() => action.value?.meta?.prompt)

const availableDates = computed<SelectableItemBase[]>(() => {
  const options = action.value?.options?.dates ?? []
  const today = TixDate.today(getTimezone())

  return options
    .filter((date): date is WithRequired<typeof date, 'date'> => date.status === 'available' && date.date !== undefined)
    .map((date) => {
      const value = date.date
      const tixDate = new TixDate(value)
      return {
        id: value,
        title: tixDate.formatTodayTomorrow(today),
      }
    })
})

watchEffect(() => {
  // Auto-select if there's only one available date
  if (availableDates.value.length === 1 && !selectedDate.value) {
    selectedDate.value = availableDates.value[0].id
  }
})

const firstSeveralAvailableDates = computed<SelectableItemBase[]>(() => {
  return availableDates.value.slice(0, MAX_CARDS)
})

const hasMoreDates = computed(() => availableDates.value.length > MAX_CARDS)

/**
 * Returns the selected date item if it was selected from the calendar
 * (i.e., not visible in the first 8 dates).
 */
const calendarSelectedItem = computed<SelectableItemBase | null>(() => {
  if (!selectedDate.value) return null

  const isInVisibleItems = firstSeveralAvailableDates.value.some((item) => item.id === selectedDate.value)
  if (isInVisibleItems) return null

  // Find the selected date in the full list
  return availableDates.value.find((item) => item.id === selectedDate.value) ?? null
})

// TODO - Improve wording of user-facing error messaging - https://ticketure-team.atlassian.net/browse/TIC-5452
const errorMessage = computed<string | null>(() => {
  if (!action.value) return `Action is not a ${actionType} action`
  if (availableDates.value.length === 0) return 'No available dates to select.'
  if (validationError.value) return validationError.value.message ?? 'Validation error'
  return null
})

function handleSelect(id: string): void {
  validationError.value = null
  if (selectedDate.value === id) return // re-click clears error but isn't a tracked change

  selectedDate.value = id
  trackBundleStep(id)
}

async function handleShowMoreDates(): Promise<void> {
  openCalendarPickerModal({
    dates: action.value?.options?.dates ?? [],
    title: title.value ?? 'Choose a date',
    onSelect: handleSelect,
  })
}

function isSelected(id: string): boolean {
  return selectedDate.value === id
}
</script>

<template>
  <section class="pick-date-action">
    <template v-if="action">
      <ActionHeader id="pick-date-header" :description="prompt" :title="title" />

      <div aria-labelledby="pick-date-header" class="date-selection-section" role="radiogroup">
        <TransitionGroup appear class="items" name="transition-group-item" tag="div">
          <SelectableCard
            v-for="(date, index) in firstSeveralAvailableDates"
            :id="date.id"
            :key="date.id"
            :isSelected="isSelected(date.id)"
            role="radio"
            :style="{ '--index': index }"
            :title="date.title"
            @click="handleSelect"
          />
        </TransitionGroup>

        <div v-if="hasMoreDates" class="show-more-container">
          <BaseButton hierarchy="secondary" intent="neutral" size="sm" @click="handleShowMoreDates">
            Show more dates
          </BaseButton>
        </div>
      </div>

      <div v-if="calendarSelectedItem" class="calendar-selection">
        <p class="calendar-selection-label">Your selected date (from calendar):</p>
        <div class="items">
          <SelectableCard
            :id="calendarSelectedItem.id"
            class="item"
            :isDisabled="true"
            :isSelected="true"
            :title="calendarSelectedItem.title"
          />
        </div>
      </div>
    </template>

    <StatusMessage :icon="IconExclamationCircle" :message="errorMessage" variant="error" />
  </section>
</template>

<style scoped>
.pick-date-action {
  display: flex;
  flex-direction: column;
  gap: var(--space-0-5);

  .items {
    display: grid;
    grid-template-columns: 1fr;
    gap: var(--space-1);

    @media (--desktop-experience) {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  .items :deep(.selectable-card) {
    height: 64px;

    .content {
      justify-content: center;
    }
  }

  .show-more-container {
    display: flex;
    justify-content: center;
    margin-top: var(--space-1);
  }

  .date-selection-section {
    display: flex;
    flex-direction: column;
  }

  .calendar-selection {
    display: flex;
    flex-direction: column;
    gap: var(--space-0-75);
    padding-top: var(--space-1-5);
    margin-top: var(--space-1-5);
    border-top: var(--divider);

    /* Display-only card: disabled but should look selected, not faded */
    :deep(.selectable-card) {
      opacity: 1;
    }
  }

  .calendar-selection-label {
    font-size: var(--size-md);
    font-weight: var(--font-weight-semibold);
    line-height: 1.4;
    color: var(--text-primary);
    margin: 0;
  }

  .transition-group-item-enter-active {
    transition:
      opacity 0.4s ease,
      transform 0.4s ease;
    transition-delay: calc(180ms * (var(--index, 0) / (var(--index, 0) + 2)));
  }
  .transition-group-item-enter-from {
    opacity: 0;
    transform: translateY(30px);
  }
}
</style>
